/**
 * One item of an access case: the form it filed, the team that has it, where
 * it stands, and its approvals — what an auditor asks for (5 Oct).
 *
 * Backoffice records approvals on the Ivanti request (the approval records,
 * and on Business Systems Access their own "approval obtained / approver /
 * date" answers); the tracker only shows them. A New case's steps (5 Oct)
 * wait for the hire's account, then show their owner — Backoffice, the
 * manager, local IT — with "Action for you" and the way to the prefilled form
 * for whoever that is; what HR said about the item is shown under it.
 *
 * Business Systems Access (5 Oct): each pick's approver from the matrix and
 * its decision; the approver gets Approve / Reject here, and Backoffice
 * "grant" once every approval is in.
 *
 * 5 Oct, product owner's UAT: each row says what it waits on ("Waiting on
 * Marcus Montie to approve: 365FO Sales for USL") or, once finished, who
 * approved it and when.
 *
 * Leave (6 Oct): a removal waits for HR to confirm, then for its team; the
 * Deactivate request is last, and says local IT disables the account then.
 */

import { Alert, Badge, Button, Typography } from "@norican/react";
import { Link } from "react-router";

import { CopyLinkMenu, useDateFormatter } from "@/components/common";
import { starterFormPath } from "@/features/starters";
import { useFeedbackToast } from "@/features/tickets";
import { useT } from "@/i18n";
import { type AccessCase, type AccessItemView, type AccessRejection, useMe } from "@/lib/api";

import { useAcknowledgeRejection } from "../api";
import {
  accessItemPath,
  canOpenStep,
  DEACTIVATE_ITEM,
  finishedLineKind,
  IT_SETUP_ITEM,
  itemLabelKey,
  showsStageBadge,
  stageWordsKey,
  withAccountStateKey,
} from "../lib/access";
import { ApprovalNeeds } from "./approval-needs";
import { JourneyBar } from "./journey-bar";
import { StatusBadge } from "./status-badge";
import { useWaitingWords } from "./waiting-on";

export interface AccessItemRowProps {
  caseId: string;
  item: AccessItemView;
  personName: string;
  /** Show the approval records and decision cards (the case page), not just badges. */
  detailed?: boolean;
  /** A closed request with no follow-up (5 Oct): nothing to open. */
  readOnly?: boolean;
  /** Leave (6 Oct): the leaving date (ISO), for the account step's line. */
  leavingDate?: string | null;
  /** The case page's case: its approvals become decision cards (19 Oct). */
  access?: AccessCase;
  /** Opened from a link to this item (`#item-n`): its first decision takes focus. */
  focused?: boolean;
}

export function AccessItemRow({
  caseId,
  item,
  personName,
  detailed = false,
  readOnly = false,
  leavingDate = null,
  access,
  focused = false,
}: AccessItemRowProps) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const { data: me } = useMe();
  // 28 Oct (Rusty): on the new forms, no offering name, raw Ivanti approval
  // records or old-form answers (the approval needs say it); the old forms
  // keep them. The "needs attention" caption is for admins.
  const newForms = me?.access_new_forms === true;
  const admin = me?.role === "admin";
  const approval = item.approval;
  const request = item.request;
  const setupKey = withAccountStateKey(item);
  const stateWords = setupKey
    ? t(setupKey as never)
    : item.state === "held"
      ? t("access.state.held", { name: personName })
      : item.state === "needs_attention"
        ? t("access.state.needs_attention")
        : item.state === "ready"
          ? t("access.state.ready", { owner: item.owner ?? "—" })
          : item.state === "it_setup"
            ? t("access.state.it_setup")
            : item.state === "awaiting_validation"
              ? t("access.state.awaiting_validation")
              : item.state === "not_needed"
                ? t("access.state.not_needed")
                : item.state === "awaiting_approval"
                  ? t("access.state.awaiting_approval")
                  : item.state === "rejected"
                    ? t("access.state.rejected")
                    : item.state === "not_requested"
                      ? t("access.state.not_requested")
                      : request?.provider_status || t("access.state.filed");
  // The IT Setup is raised from the new starter's own form flow; every other
  // step opens its form prefilled from the case.
  const stepPath =
    item.item === IT_SETUP_ITEM
      ? starterFormPath(caseId, "it_setup")
      : accessItemPath(caseId, item.n);
  // 22 Oct: "Open and complete" only for whoever completes the step.
  const canOpen = canOpenStep(item, readOnly);
  const words = useWaitingWords();
  const waits = item.waiting_on ?? [];
  const doneLine = item.finished ? finishedLine() : null;

  /** "Done: approved by X on <date>", else "Done: <status>, <date>"; never "Done: Done, —". */
  function finishedLine(): string {
    const approved = (item.needs ?? []).filter((need) => need.state === "approved");
    const decided =
      approved.length > 0
        ? approved.map((need) => ({ who: need.decided_by, at: need.decided_at }))
        : (item.approval?.records ?? [])
            .filter((record) => record.status === "APPROVED")
            .map((record) => ({ who: record.approver, at: record.decided_at }));
    const names = [...new Set(decided.map((entry) => entry.who).filter(Boolean))].join(", ");
    const latest = decided
      .map((entry) => entry.at)
      .filter((at): at is string => typeof at === "string")
      .sort()
      .at(-1);
    const line = finishedLineKind({
      state: item.state,
      request,
      finished_at: item.finished_at,
      approvedNames: names,
    });
    switch (line.key) {
      case "approved":
        return t("access.waiting.doneApproved", {
          names,
          date: latest ? formatDate(new Date(latest)) : "—",
        });
      case "withAccount":
        return t("access.waiting.doneWithAccount");
      case "done":
        return t("access.waiting.done");
      case "status":
        return t("access.waiting.doneStatusOnly", { status: line.status });
      case "statusDate":
        return t("access.waiting.doneStatus", {
          status: line.status,
          date: formatDate(new Date(item.finished_at ?? "")),
        });
    }
  }

  // 19 Oct (UI review, finding 3): at most two badges, the status and
  // "Action for you"; the rest is words on the line below.
  const details = [
    item.action ? t(`access.action.${item.action}`) : null,
    showsStageBadge(item) ? t(stageWordsKey(item) as never) : null,
    item.no_approval_needed ? t("access.approval.notNeeded") : null,
    approval?.state !== undefined && approval.state !== "none" && (item.needs ?? []).length === 0
      ? t(`access.approval.${approval.state}`)
      : null,
  ].filter((part): part is string => part !== null && part !== "");

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <Typography as="span" variant="body-sm" className="font-medium">
          {t(itemLabelKey(item.item) as never)}
        </Typography>
        <StatusBadge domain="item" status={item.state}>
          {stateWords}
        </StatusBadge>
        {item.action_for_you ? (
          <Badge
            tone={item.your_action === "rejected" ? "danger" : "accent"}
            variant="solid"
            size="xs"
          >
            {item.your_action === "approve"
              ? t("access.actionApprove")
              : item.your_action === "grant"
                ? t("access.actionGrant")
                : item.your_action === "rejected"
                  ? t("access.actionRejected")
                  : t("access.actionForYou")}
          </Badge>
        ) : null}
      </span>
      {/* 29 Oct: this ticket's own steps, on the case page. */}
      {detailed && (item.steps ?? []).length > 0 ? (
        <JourneyBar steps={item.steps ?? []} variant="mini" item />
      ) : null}
      {details.length > 0 ? (
        <Typography as="span" variant="body-sm" className="min-w-0 break-words">
          {details.join(" · ")}
        </Typography>
      ) : null}
      <Typography as="span" variant="body-sm" className="min-w-0 break-words">
        {[newForms ? null : item.offering, item.team ? t("access.team", { team: item.team }) : null]
          .filter(Boolean)
          .join(" · ")}
        {/* The team once (14 Oct): "then <owner>" only when someone else takes it on. */}
        {item.owner && item.state === "held" && item.owner !== item.team
          ? ` · ${t("access.then", { owner: item.owner })}`
          : ""}
        {request && request.ticket_number ? (
          <>
            {newForms && !item.team ? "" : " · "}
            <Link to={request.url_path} className="underline underline-offset-2">
              {t("access.request", { number: request.ticket_number })}
            </Link>{" "}
            {/* 23 Oct: IT shares the request with a colleague; hidden for everyone else. */}
            <span className="inline-flex align-middle">
              <CopyLinkMenu
                variant="icon"
                kind="SERVICE_REQUEST"
                number={request.ticket_number}
                summary={item.offering}
                helpdesk={false}
              />
            </span>
          </>
        ) : null}
      </Typography>
      {/* 29 Oct: on the case page the approvals table says who it waits on. */}
      {waits.length > 0 &&
      !(detailed && (item.needs ?? []).length > 0 && waits.every((w) => w.what === "approval")) ? (
        <Typography as="span" variant="body-sm" className="min-w-0 font-medium break-words">
          {waits.map(words.itemEntry).join(" · ")}
        </Typography>
      ) : doneLine ? (
        <Typography as="span" variant="body-sm" tone="muted" className="min-w-0 break-words">
          {doneLine}
        </Typography>
      ) : null}
      {item.item === DEACTIVATE_ITEM && !item.finished ? (
        <Typography as="span" variant="body-sm" className="min-w-0 break-words">
          {t("access.leave.disableLast", {
            date: leavingDate ? formatDate(new Date(`${leavingDate}T12:00:00`)) : "—",
          })}
        </Typography>
      ) : null}
      {item.rejection ? (
        <RejectionNotice caseId={caseId} n={item.n} rejection={item.rejection} />
      ) : null}
      {item.note ? (
        <Typography as="span" variant="caption" className="min-w-0 break-words whitespace-pre-line">
          {item.note}
        </Typography>
      ) : null}
      {canOpen ? (
        <Button size="sm" variant="solid" className="self-start" render={<Link to={stepPath} />}>
          {item.item === IT_SETUP_ITEM ? t("access.raiseItSetup") : t("access.openStep")}
        </Button>
      ) : null}
      {detailed && access !== undefined && (item.needs ?? []).length > 0 ? (
        <ApprovalNeeds access={access} item={item} focused={focused} />
      ) : null}
      {detailed && approval && !newForms ? (
        <>
          {(approval.records ?? []).map((record, index) => (
            <Typography
              key={`${record.name ?? ""}-${index}`}
              as="span"
              variant="caption"
              tone="muted"
            >
              {t("access.approvalRecord", {
                name: record.name ?? t("access.approvalName"),
                status: t(`access.approvalStatus.${record.status}` as never),
                approver: record.approver ?? "—",
                date: record.decided_at ? formatDate(new Date(record.decided_at)) : "—",
              })}
            </Typography>
          ))}
          {approval.tracking ? (
            <Typography as="span" variant="caption" tone="muted">
              {t("access.tracking", {
                obtained: approval.tracking.obtained_text ?? "—",
                approver: approval.tracking.approver ?? "—",
                date: approval.tracking.date_text ?? "—",
              })}
            </Typography>
          ) : null}
        </>
      ) : null}
      {detailed && admin && item.state === "needs_attention" && item.attention ? (
        <Typography as="span" variant="caption" tone="muted">
          {item.attention}
        </Typography>
      ) : null}
    </div>
  );
}

/**
 * 19 Oct: a rejection made obvious: who rejected it, when and why, who must
 * follow up (the step's sender), and "Mark as seen" for them (or an admin).
 */
function RejectionNotice({
  caseId,
  n,
  rejection,
}: {
  caseId: string;
  n: number;
  rejection: AccessRejection;
}) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const toast = useFeedbackToast();
  const seen = useAcknowledgeRejection(caseId, n);
  const open = !rejection.seen_at;
  return (
    <Alert tone={open ? "danger" : "neutral"}>
      <span className="flex min-w-0 flex-col gap-1">
        <strong className="break-words">
          {t("access.rejection.title", {
            name: rejection.rejected_by,
            date: formatDate(new Date(rejection.rejected_at)),
          })}
        </strong>
        <span className="break-words">
          {rejection.reason
            ? t("access.rejection.reason", { reason: rejection.reason })
            : t("access.rejection.noReason")}
        </span>
        {open && rejection.sent_by ? (
          <span className="break-words">
            {t("access.rejection.followUp", { name: rejection.sent_by })}
          </span>
        ) : null}
        {!open && rejection.seen_by ? (
          <span className="break-words">
            {t("access.rejection.seen", {
              name: rejection.seen_by,
              date: formatDate(new Date(rejection.seen_at ?? "")),
            })}
          </span>
        ) : null}
        {open && rejection.can_acknowledge ? (
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            disabled={seen.isPending}
            onClick={() =>
              seen.mutateAsync().then(
                () => toast.success(t("access.rejection.seenToast")),
                () => undefined,
              )
            }
          >
            {t("access.rejection.markSeen")}
          </Button>
        ) : null}
      </span>
    </Alert>
  );
}
